"use client";
import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../msalConfigs";
import { useState, useEffect, useCallback } from "react";

export const useAuth = () => {
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const cleanupUrlHash = useCallback(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      // Check if hash contains authentication data
      if (
        window.location.hash.includes("#code=") ||
        window.location.hash.includes("#error=") ||
        window.location.hash.includes("#state=")
      ) {
        // Remove the hash without causing a page reload
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState(null, document.title, cleanUrl);
        console.log("Cleaned up authentication hash from URL");
      }
    }
  }, []);

  // Initialize MSAL on component mount
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Dynamically import MSAL to avoid SSR issues
        const { msalInstance: instance } = await import(
          "../providers/SessionProvider"
        );

        if (!instance) {
          console.error("MSAL instance is not available");
          setAuthError("Authentication service unavailable");
          return;
        }
        // Initialize MSAL
        await instance.initialize();
        setMsalInstance(instance);
        setIsInitialized(true);

        // Handle any existing redirect response
        try {
          setIsAuthenticating(true);
          const response = await instance.handleRedirectPromise();
          if (response) {
            console.log("Successfully handled redirect");
            cleanupUrlHash();
          }
        } catch (error) {
          console.error("Error handling redirect:", error);
          setAuthError("Failed to complete authentication redirect");
        } finally {
          setIsAuthenticating(false);
        }
      } catch (error) {
        console.error("Error initializing authentication:", error);
        setAuthError("Failed to initialize authentication");
      }
    };

    initializeMsal();
  }, [cleanupUrlHash]);
  // Get access token function using useCallback to maintain reference stability
  const getAccessToken = useCallback(async () => {
    // Clear any previous errors
    setAuthError(null);

    if (!isInitialized || !msalInstance) {
      console.log("MSAL not yet initialized, waiting...");
      return null;
    }

    try {
      // Get all accounts
      const accounts = msalInstance.getAllAccounts();

      // If no accounts, initiate login flow
      if (accounts.length === 0) {
        console.log("No accounts found, initiating login redirect");
        setIsAuthenticating(true);

        try {
          await msalInstance.loginRedirect({
            ...loginRequest,
            redirectUri: window.location.origin,
          });
          // Page will redirect, code won't continue past this point
          return null;
        } catch (error) {
          console.error("Login redirect error:", error);
          setIsAuthenticating(false);
          setAuthError("Failed to start login process");
          return null;
        }
      }

      // We have an account, try to get token silently
      try {
        console.log("Account found, acquiring token silently");
        const response = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: loginRequest.scopes,
        });
        cleanupUrlHash();
        return response.accessToken;
      } catch (silentError: any) {
        // Silent token acquisition failed, try interactive
        console.warn("Silent token acquisition failed:", silentError);

        if (silentError.name === "InteractionRequiredAuthError") {
          console.log("Interaction required, initiating redirect");
          setIsAuthenticating(true);
          try {
            await msalInstance.acquireTokenRedirect({
              account: accounts[0],
              scopes: loginRequest.scopes,
              redirectUri: window.location.origin,
            });
            // Page will redirect, code won't continue past this point
            return null;
          } catch (redirectError) {
            console.error("Token redirect error:", redirectError);
            setIsAuthenticating(false);
            setAuthError("Failed to refresh authentication");
            return null;
          }
        } else {
          console.error("Unexpected token acquisition error:", silentError);
          setAuthError("Failed to get access token");
          return null;
        }
      }
    } catch (error: any) {
      console.error("Unexpected error in getAccessToken:", error);
      setAuthError(error.message || "Authentication failed");
      return null;
    }
  }, [isInitialized, msalInstance, cleanupUrlHash]);
  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    if (!msalInstance) return false;
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0;
  }, [msalInstance]);

  // Logout function
  const logout = useCallback(async () => {
    if (!msalInstance) return;

    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout error:", error);
      setAuthError("Failed to logout");
    }
  }, [msalInstance]);

  return {
    getAccessToken,
    isAuthenticated,
    logout,
    isAuthenticating,
    isInitialized,
    authError,
  };
};
